import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { KnowledgeBaseArticle, FilterOptions } from '../types';

const KnowledgeBase: React.FC = () => {
  const [filters, setFilters] = useState<FilterOptions>({
    category: 'all',
    type: 'all',
    difficulty: 'all',
    searchQuery: ''
  });

  const categories = [
    { id: 'all', name: 'All Categories', icon: 'Grid' },
    { id: 'getting-started', name: 'Getting Started', icon: 'Rocket' },
    { id: 'integration', name: 'Integration', icon: 'Plug' },
    { id: 'api', name: 'API Reference', icon: 'Code' },
    { id: 'best-practices', name: 'Best Practices', icon: 'Award' },
    { id: 'troubleshooting', name: 'Troubleshooting', icon: 'AlertCircle' }
  ];

  const articles: KnowledgeBaseArticle[] = [
    {
      id: '1',
      title: 'Quick Start Guide: Deploy Your First Analytics Instance',
      excerpt: 'Learn how to set up and configure camlytix AI in under 30 minutes with our comprehensive quick start guide.',
      category: 'getting-started',
      readTime: '8 min read',
      helpful: 342,
      views: 5420,
      lastUpdated: '2024-01-15'
    },
    {
      id: '2',
      title: 'Integrating with Existing Surveillance Systems',
      excerpt: 'Step-by-step instructions for connecting camlytix AI to your current camera infrastructure and CCTV systems.',
      category: 'integration',
      readTime: '12 min read',
      helpful: 289,
      views: 4180,
      lastUpdated: '2024-01-14'
    },
    {
      id: '3',
      title: 'REST API Authentication and Authorization',
      excerpt: 'Complete guide to implementing secure API authentication using JWT tokens and OAuth 2.0 protocols.',
      category: 'api',
      readTime: '15 min read',
      helpful: 456,
      views: 6230,
      lastUpdated: '2024-01-13'
    },
    {
      id: '4',
      title: 'Optimizing Analytics Accuracy for Retail Environments',
      excerpt: 'Best practices for calibrating AI models to achieve maximum accuracy in retail footfall and behavior analysis.',
      category: 'best-practices',
      readTime: '10 min read',
      helpful: 198,
      views: 3450,
      lastUpdated: '2024-01-12'
    },
    {
      id: '5',
      title: 'Resolving Common Camera Connection Issues',
      excerpt: 'Troubleshooting guide for fixing RTSP stream problems, network connectivity issues, and camera compatibility challenges.',
      category: 'troubleshooting',
      readTime: '7 min read',
      helpful: 523,
      views: 7890,
      lastUpdated: '2024-01-11'
    },
    {
      id: '6',
      title: 'Advanced Analytics Configuration for Traffic Management',
      excerpt: 'Configure vehicle detection, traffic flow analysis, and ATCC compliance for smart city deployments.',
      category: 'integration',
      readTime: '18 min read',
      helpful: 167,
      views: 2340,
      lastUpdated: '2024-01-10'
    },
    {
      id: '7',
      title: 'WebSocket API for Real-Time Analytics Streaming',
      excerpt: 'Implement real-time analytics data streaming using WebSocket connections for live dashboard updates.',
      category: 'api',
      readTime: '14 min read',
      helpful: 234,
      views: 3890,
      lastUpdated: '2024-01-09'
    },
    {
      id: '8',
      title: 'Data Privacy and GDPR Compliance Guidelines',
      excerpt: 'Ensure your camlytix AI deployment meets international data protection regulations and privacy standards.',
      category: 'best-practices',
      readTime: '11 min read',
      helpful: 412,
      views: 5670,
      lastUpdated: '2024-01-08'
    }
  ];

  const filteredArticles = articles.filter(article => {
    const matchesCategory = filters.category === 'all' || article.category === filters.category;
    const matchesSearch = filters.searchQuery === '' || 
      article.title.toLowerCase().includes(filters.searchQuery.toLowerCase()) ||
      article.excerpt.toLowerCase().includes(filters.searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryIcon = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.icon || 'FileText';
  };

  return (
    <section id="knowledge-base" className="py-16 px-4 md:px-6 lg:px-8 bg-surface">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
            Knowledge Base
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Searchable documentation library with comprehensive guides, tutorials, and best practices for every aspect of camlytix AI.
          </p>
        </div>

        <div className="mb-8">
          <div className="max-w-2xl mx-auto">
            <Input
              type="search"
              placeholder="Search documentation, guides, and tutorials..."
              value={filters.searchQuery}
              onChange={(e) => setFilters({ ...filters, searchQuery: e.target.value })}
              className="w-full"
            />
          </div>
        </div>

        <div className="flex flex-wrap gap-3 justify-center mb-12">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setFilters({ ...filters, category: category.id })}
              className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
                filters.category === category.id
                  ? 'bg-primary text-primary-foreground shadow-brand'
                  : 'bg-card text-text-secondary hover:bg-muted border border-border'
              }`}
            >
              <Icon name={category.icon as any} size={18} />
              <span>{category.name}</span>
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredArticles.map((article) => (
            <div
              key={article.id}
              className="bg-card rounded-xl border border-border p-6 hover:shadow-brand-lg transition-all duration-300 group cursor-pointer"
              onClick={() => console.log(`Opening article: ${article.title}`)}
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-2">
                  <div className="p-2 bg-primary/10 rounded-lg">
                    <Icon name={getCategoryIcon(article.category) as any} size={20} className="text-primary" />
                  </div>
                  <span className="text-xs font-medium text-text-secondary uppercase tracking-wide">
                    {categories.find(cat => cat.id === article.category)?.name}
                  </span>
                </div>
                <Icon name="ExternalLink" size={16} className="text-text-secondary group-hover:text-primary transition-colors duration-300" />
              </div>

              <h3 className="text-lg font-bold text-foreground mb-3 group-hover:text-primary transition-colors duration-300">
                {article.title}
              </h3>

              <p className="text-sm text-text-secondary mb-4 line-clamp-2">
                {article.excerpt}
              </p>

              <div className="flex items-center justify-between pt-4 border-t border-border">
                <div className="flex items-center space-x-4 text-xs text-text-secondary">
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={14} />
                    <span>{article.readTime}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Eye" size={14} />
                    <span>{article.views.toLocaleString()}</span>
                  </div>
                </div>
                <div className="flex items-center space-x-1 text-xs text-success">
                  <Icon name="ThumbsUp" size={14} />
                  <span>{article.helpful}</span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {filteredArticles.length === 0 && (
          <div className="text-center py-12">
            <Icon name="Search" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">No articles found</h3>
            <p className="text-text-secondary mb-6">
              Try adjusting your search or filter criteria
            </p>
            <Button
              variant="outline"
              size="default"
              onClick={() => setFilters({ category: 'all', type: 'all', difficulty: 'all', searchQuery: '' })}
            >
              Clear Filters
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default KnowledgeBase;